import { useCallback, type KeyboardEvent } from 'react'

import { ModalCardStyled } from './styles/ModalCardStyled'
import { type ICardStyledProps } from '@ui/elements/Card/styles/CardStyled'

/*
 * Tab-reachable elements for focus wrapping.
 * Note: :not([tabindex="-1"]) is used on each selector, not just the [tabindex] clause,
 * because CSS selector lists are OR'd. Hidden inputs (react-select renders one per
 * `name`) are never focusable, so one after the last control would stop the wrap.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/*
 * An element with no box (display: none on it or an ancestor) or with
 * visibility: hidden cannot take focus. Where checkVisibility() is missing
 * (older browsers, jsdom) every match counts as rendered.
 */
const isRendered = (element: HTMLElement) =>
  typeof element.checkVisibility !== 'function' ||
  element.checkVisibility({
    checkVisibilityCSS: true,
    visibilityProperty: true,
  })

export interface IModalCardProps extends ICardStyledProps {
  /**
   * When true, intercepts Tab and Shift+Tab at the focus boundaries of the card
   * and wraps focus between the first and last focusable elements.
   *
   * Use this when the modal contains a meaningful Tab sequence that MUI's
   * TrapFocus sentinel elements would otherwise interrupt, causing a dead stop
   * before cycling back to the first focusable element, or when the modal
   * disables MUI's focus enforcement (as `ModalDialog` does).
   *
   * Hidden inputs and elements that are not rendered are skipped, and a Tab
   * that a control inside the card has already handled (`preventDefault`, as
   * react-select does when Tab picks the highlighted option) is left alone.
   */
  cycleFocus?: boolean
}

export const ModalCard = ({
  cycleFocus,
  onKeyDown,
  ...props
}: IModalCardProps) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (cycleFocus && e.key === 'Tab' && !e.defaultPrevented) {
        const focusable = Array.from(
          e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter(isRendered)
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first?.focus()
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last?.focus()
        }
      }
      onKeyDown?.(e)
    },
    [cycleFocus, onKeyDown]
  )

  return <ModalCardStyled onKeyDown={handleKeyDown} {...props} />
}

ModalCard.displayName = 'Modal.Card'
